import { act, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Box, Button, Checkbox, Typography } from "@mui/material";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
// import { ReactComponent as Draft } from "../../assets/images/images-svg/File.svg";
import { ReactComponent as Favorite } from "../../assets/images/images-svg/Star.svg";
import { ReactComponent as Sent } from "../../assets/images/images-svg/send.svg";
import { ReactComponent as Spam } from "../../assets/images/images-svg/Spam.svg";
import { ReactComponent as Trash } from "../../assets/images/images-svg/Trash 2.svg";
import Delete from "../../assets/images/images-svg/DeleteOutlined.svg";
// import Mail from "../../assets/images/images-svg/Mail.svg";
import { ReactComponent as InboxIcon } from "../../assets/images/images-svg/Inbox.svg";
import { formatTimeStampDate, getInitials } from "src/constants/common";
import store, { useAppDispatch, useAppSelector } from "src/redux/store";
import {
  DeleteMultipleEmails,
  GetAllEmails,
  GetAllFolders,
  setPageNumber,
  setSelectedFolderId,
  UpdateEmailStatus,
} from "src/redux/features/mailBox/mailbox-slice";
import "./emails.scss";
import "../../components/common/Pagination.scss";
import { IFolder } from "src/types/mail";
import { ReactComponent as LeftArrow } from "../../assets/images/images-svg/leftArrow.svg";
import { ReactComponent as RightArrow } from "../../assets/images/images-svg/rightArrow.svg";

interface IEmailProps {
  userEmail: string;
}

export const emailFolders = [
  { gmailName: "Inbox", icon: <InboxIcon />, index: 0, outlookname: "Inbox" },
  // { name: "Draft", icon: <Draft />, index: 1 },
  { gmailName: "Starred", icon: <Favorite />, index: 1, outlookname: "none" },
  { gmailName: "Sent", icon: <Sent />, index: 2, outlookname: "Sent Items" },
  { gmailName: "Spam", icon: <Spam />, index: 3, outlookname: "Junk Email" },
  {
    gmailName: "Trash",
    icon: <Trash />,
    index: 4,
    outlookname: "Deleted Items",
  },
];

const Emails: React.FC<IEmailProps> = ({ userEmail }) => {
  // #region variables region

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  //selected emails ids
  const [selectedEmailIds, setSelectedEmailIds] = useState<string[]>([]);

  //stores folders
  const {
    allEmails,
    folders,
    paginationData,
    pageNumber,
    selectedFolder,
    mailBoxAuthData,
    next_cursor,
  } = useAppSelector((state) => state.mailBoxSlice);

  //pagination variables
  const limit = 20; // Assuming each page has 20 items
  const [activeIndex, setActiveIndex] = useState<number>(pageNumber);
  const [lastIndex, setLastIndex] = useState<number>(0);
  const [pageRange, setPageRange] = useState<string>("1 - 20"); // Initial range for the first page

  // #endregion

  // #region methods region
  const handleFolderClick = (folder: IFolder) => {
    dispatch(setSelectedFolderId(folder));
    setActiveIndex(1); // Reset to first page when folder changes
    // if (folder?.id === "DRAFT") {
    //   dispatch(
    //     GetAllDrafts({
    //       pageToken: "",
    //       userEmail: userEmail,
    //     })
    //   );
    // } else {
    //   dispatch(
    //     GetAllEmails({
    //       userEmail: userEmail,
    //       pageToken: "", //initially no token
    //       folderId: folder?.id,
    //     })
    //   );
    // }
  };
  function handlePageChange(type: "Right" | "Left") {
    let pageToken = "";
    const currentIndex = activeIndex + (type === "Right" ? 1 : -1);
    setActiveIndex(currentIndex);
    dispatch(setPageNumber(currentIndex));
    if (type === "Right") {
      pageToken = store.getState().mailBoxSlice.next_cursor;
    } else if (type === "Left") {
      const previousToken = paginationData.find(
        (item) => item.pageIndex === currentIndex
      )?.pageToken;
      pageToken = previousToken || "";
    }

    dispatch(
      GetAllEmails({
        userEmail: userEmail,
        pageToken: pageToken,
        folderId: selectedFolder?.id || "",
      })
    );

    handlePageRange(currentIndex - 1);
  }

  function handlePageRange(activePage: number) {
    if (!selectedFolder || !selectedFolder?.totalCount) return;
    const startRange = activePage * limit + 1;
    const endRange = startRange + limit - 1;
    if (selectedFolder?.totalCount < endRange) {
      setPageRange(`${startRange} - ${selectedFolder?.totalCount}`);
    } else {
      setPageRange(`${startRange} - ${endRange}`);
    }
    return;
  }

  // #endregion

  // #region useEffect region
  useEffect(() => {
    if (mailBoxAuthData) {
      dispatch(GetAllFolders({ userEmail: mailBoxAuthData.email }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailBoxAuthData]);

  useEffect(() => {
    if (!mailBoxAuthData || !selectedFolder || next_cursor !== "") return;
    dispatch(
      GetAllEmails({
        userEmail: mailBoxAuthData.email,
        pageToken: "",
        folderId: selectedFolder?.id,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailBoxAuthData, selectedFolder?.id]);

  useEffect(() => {
    if (!selectedFolder) return;
    handlePageRange(pageNumber - 1);
    setLastIndex(Math.ceil(selectedFolder?.totalCount / limit) || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder?.totalCount]);

  return (
    <div className="emailContainer">
      <div className="email-folders">
        <Typography className="title">Mailbox</Typography>
        <Typography className="useremail">{mailBoxAuthData?.email}</Typography>

        {folders.map((folder) => (
          <div
            className={
              selectedFolder?.id === folder.id ? "folder active" : "folder"
            }
            key={folder.id}
            onClick={() => handleFolderClick(folder)}
          >
            <div className="folder-details">
              {folder?.icon}
              <Typography>{folder?.displayName}</Typography>
            </div>
            {folder.unreadCount !== 0 && (
              <Typography className="unread-count">
                {folder.unreadCount}
              </Typography>
            )}
          </div>
        ))}
      </div>
      <div className="all-emails">
        <div className="email-actions">
          <div className="email-icons">
            {(selectedFolder?.displayName !== "Trash" &&
              selectedFolder?.displayName !== "Deleted Items") && (
              <img
                src={Delete}
                alt=""
                onClick={() => {
                  if (!mailBoxAuthData?.email || !selectedEmailIds) return;
                  dispatch(
                    DeleteMultipleEmails({
                      userEmail: mailBoxAuthData?.email,
                      emailIds: selectedEmailIds,
                    })
                  );
                  setSelectedEmailIds([]);
                }}
              />
            )}

            {/* <img src={Mail} alt="" onClick={() => {}} /> */}
          </div>
          <Button
            variant="contained"
            onClick={() => navigate("/compose-email")}
          >
            Compose
          </Button>
        </div>
        <div className="email-list">
          {allEmails.length ? (
            allEmails.map((item) => (
              <div
                key={item.id}
                className="email-item"
                onClick={() => {
                  if (selectedFolder?.id === "DRAFT") {
                    navigate(`/draft/${item.id}`);
                  } else {
                    navigate(`/mail/${item.id}`);
                    if (item.unread) {
                      dispatch(
                        UpdateEmailStatus({
                          email: userEmail,
                          mailId: item.id,
                          unread: false,
                        })
                      );
                    }
                    //get threads
                    dispatch(
                      GetAllEmails({
                        userEmail,
                        pageToken: "",
                        threadId: item?.threadId,
                      })
                    );
                  }
                }}
              >
                {(selectedFolder?.displayName !== "Trash" && selectedFolder?.displayName !== "Deleted Items") && (
                  <>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedEmailIds.includes(item.id)) {
                          setSelectedEmailIds(
                            selectedEmailIds.filter((id) => id !== item.id)
                          );
                        } else {
                          setSelectedEmailIds([...selectedEmailIds, item.id]);
                        }
                      }}
                    >
                      <Checkbox />
                    </span>
                    {/* {mailBoxAuthData?.provider !== "microsoft" && ( */}
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(
                              UpdateEmailStatus({
                                email: userEmail,
                                mailId: item.id,
                                starred: !item.starred,
                              })
                            );
                          }}
                          checked={item.starred}
                          icon={<StarBorderIcon />} // Empty star
                          checkedIcon={<StarIcon className="fav-filled" />} // Filled star
                        />
                      </span>
                    {/* // )} */}
                  </>
                )}

                <Avatar sx={{ bgcolor: "#096dd9", color: "white" }}>
                  {item.from[0].name ? getInitials(item?.from[0]?.name) : "UK"}
                </Avatar>
                <div className="email-content-preview">
                  <span
                    className={
                      item.unread ? "email-subject" : "email-subject-read"
                    }
                  >
                    {item.subject}
                  </span>
                  <span className="email-content">{item.snippet}</span>
                </div>
                <span className="email-date">
                  {formatTimeStampDate(item.date, "Preview")}
                </span>
              </div>
            ))
          ) : (
            <div className="email-item">
              <span className="email-subject">No Email Found</span>
            </div>
          )}
        </div>

        {allEmails.length > 0 && selectedFolder && (
          <Box className="mail-pagination">
            <Typography className="page-range">{`${pageRange} of ${selectedFolder?.totalCount}`}</Typography>

            <span
              className="arrow-btn"
              onClick={() => {
                if (activeIndex === 1) return;
                handlePageChange("Left");
              }}
            >
              <LeftArrow
                className={activeIndex === 1 ? "arrow disabled" : "arrow"}
              />
            </span>

            <span
              className="arrow-btn"
              onClick={() => {
                if (lastIndex === activeIndex) return;
                handlePageChange("Right");
              }}
            >
              <RightArrow
                className={
                  lastIndex === activeIndex ? "arrow disabled" : "arrow"
                }
              />
            </span>
          </Box>
        )}
      </div>
    </div>
  );
};

export default Emails;
